import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { type InstallRoute, promptInstall, useInstallRoute } from './install';

/**
 * «Installa l'app» in the account disclosure (#303, part 2). The account panel is in the rail on a
 * desktop and in the drawer on a phone, so one entry serves both, and the header stays one row
 * (decision 5).
 *
 * It is there only when installing is possible and not done yet: on Chrome, Edge and Android it
 * opens the browser's own dialog; on iPhone, iPad and Safari for Mac, which have no such dialog,
 * it opens a short guide. Inside the installed app it is gone.
 *
 * The guide itself is the caller's (`AccountMenu`): it lives in a portal, outside the panel, and a
 * click in it would close the panel and take the guide with it.
 */
export function InstallAppItem({
  onDone,
  onOpenGuide,
  route: forcedRoute,
}: {
  onDone: () => void;
  onOpenGuide: (platform: GuidePlatform) => void;
  route?: InstallRoute;
}) {
  const detected = useInstallRoute();
  const route = forcedRoute ?? detected;
  const { t } = useTranslation('shell');

  if (route !== 'prompt' && route !== 'ios-guide' && route !== 'mac-guide') {
    return null;
  }

  return (
    <button
      className="account-item"
      type="button"
      onClick={() => {
        onDone();
        if (route === 'prompt') {
          void promptInstall();
        } else {
          onOpenGuide(route === 'ios-guide' ? 'ios' : 'mac');
        }
      }}
    >
      <Download aria-hidden="true" />
      {t('install.action')}
    </button>
  );
}

export type GuidePlatform = 'ios' | 'mac';

/**
 * The guide for the browsers that install from their own menu. Three numbered steps with the
 * menu's own words, in the side sheet the console already uses for reading (the privacy notice),
 * and a closing button: a phone has no Escape key.
 */
export function InstallGuide({
  platform,
  open,
  onOpenChange,
  returnFocusTo,
}: {
  platform: GuidePlatform;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opened from a panel that has closed, so Radix has no trigger to give the focus back to. */
  returnFocusTo?: { current: HTMLElement | null };
}) {
  const { t } = useTranslation('shell');
  const steps = t(`install.${platform}.steps`, { returnObjects: true });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-6 sm:max-w-md"
        onCloseAutoFocus={(event) => {
          if (returnFocusTo?.current) {
            event.preventDefault();
            returnFocusTo.current.focus();
          }
        }}
      >
        <div className="space-y-1.5">
          <SheetTitle className="text-lg">{t(`install.${platform}.title`)}</SheetTitle>
          <SheetDescription>{t('install.why')}</SheetDescription>
        </div>
        <ol className="text-foreground mt-5 list-decimal space-y-3 pl-5 text-sm leading-relaxed">
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
          {t(`install.${platform}.after`)}
        </p>
        <div className="mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('install.close')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
